<template>
	<div v-if="authenticated && !showUpdateAccount" class="backgroundColorLightGrey">
		<div class="container">
			<div class="row flexBoxCentered signInPage signedInPage textCenter">
				<a href="/">
					<img class="signInLogo paddingNormalTop paddingLargeBottom" src="/images/dark-logo.svg"
						alt="Leland Little Auctions" />
				</a>

				<transition name="fade" mode="out-in">
					<div v-if="!authenticated">
						<div class="majorTitle">Signing in...</div>
						<div class="paddingLargeLeft paddingLargeRight paddingLargeBottom marginNormalBottom">
							Please wait while we sign you in to your Leland Little account.
						</div>
					</div>
					<div v-else :key="'signed_out'">
						<div class="majorTitle">You are signed in.</div>
						<div class="paddingLargeLeft paddingLargeRight paddingLargeBottom marginNormalBottom">
							You have been successfully signed in to your Leland Little
							account.
						</div>
						<div class="row">
							<div class="twelve columns">
								<button type="submit" class="bidButton backgroundColorDefault u-full-width" @click="hideModal">
									Continue
								</button>
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>
	</div>
	<div v-else-if="twoFactorRequest.length > 0">
		<div class="container">
			<div class="row flexBoxCentered signInPage signedInPage textCenter">
				<a href="/">
					<img class="signInLogo paddingNormalTop paddingLargeBottom" src="/images/dark-logo.svg"
						alt="Leland Little Auctions" />
				</a>

				<transition name="fade" mode="out-in">
					<div>
						<div class="majorTitle">
							This account is protected with two-factor authentication.
						</div>
						<div class="textLeft paddingLargeLeft paddingLargeRight paddingLargeBottom marginNormalBottom">
							A verification code has been sent to your phone; please enter it
							below.
						</div>
						<div class="row textLeft">
							<div class="twelve columns">
								<input id="pin" v-model="pin" type="text" name="pin" required />
								<input id="two_factor_token" v-model="twoFactorRequest" type="hidden" name="two_factor_token"
									required />
							</div>
						</div>

						<div class="paddingNormalBottom">
							<ApiResponse :context="'send_two_factor_pin'" />
						</div>

						<div class="row">
							<button v-if="typeof apiPending.send_two_factor_pin !== 'undefined'"
								class="disabledButton bidButton u-full-width" disabled="disabled">
								<span class="loading" />
							</button>
							<button v-else type="submit" class="bidButton backgroundColorDefault u-full-width"
								@click="sendTwoFactorPin('send_two_factor_pin')">
								Continue
							</button>
						</div>
					</div>
				</transition>
			</div>
		</div>
	</div>
	<div v-else class="backgroundColorLightGrey">
		<div class="container">
			<div class="row flexBoxCentered textCenter">
				<a href="/" class="marginSmallBottom">
					<img class="signInLogo paddingNormalTop paddingLargeBottom" src="/images/dark-logo.svg"
						alt="Leland Little Auctions" />
				</a>
				<transition name="fade" mode="out-in">
					<div v-if="showCreateAccount" class="backgroundColorLightGrey">
						<CreateAccount :on-close="toggleCreateAccount" />
					</div>

					<div v-else-if="showUpdateAccount" class="backgroundColorLightGrey">
						<div class="majorTitle paddingNoneTop">Update your Account</div>
						<UpdateAccount />
					</div>

					<div v-else-if="authenticateWithPassword" :key="1" class="signInPage u-full-width">
						<div class="paddingLargeLeft paddingLargeRight">
							<div class="minorTitle">
								Sign in with your Leland Little Auctions account.
							</div>
							<div class="paddingNormalBottom">
								<ApiResponse :context="'log_in_with_password'" />
							</div>
						</div>
						<div class="container">
							<div class="row textLeft">
								<div class="twelve columns">
									<div>Email Address</div>
									<input id="username" v-model="credentials.username" type="text" name="username" required />
								</div>
							</div>
							<div class="row textLeft paddingNormalBottom">
								<div class="twelve columns">
									<div>Password</div>
									<input id="password" v-model="credentials.password" type="password" name="password" required
										@keyup.enter="authenticate('log_in_with_password')" />
								</div>
							</div>
							<div class="row">
								<button v-if="typeof apiPending.log_in_with_password !== 'undefined'"
									class="disabledButton bidButton u-full-width" disabled="disabled">
									<span class="loading" />
								</button>
								<button v-else type="submit" class="bidButton backgroundColorDefault u-full-width"
									@click="authenticate('log_in_with_password')">
									Sign in
								</button>
							</div>
							<div class="row marginLargeBottom">
								<div class="paddingLargeTop">
									<a class="navLink" @click="toggleCreateAccount">Create an account</a>
									&nbsp;&nbsp;/&nbsp;&nbsp;
									<a class="navLink" @click="toggleAuthenticateWithPassword">Forgot Password?</a>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="twelve columns textCenter">
								<div class="paddingLargeTop">
									<a class="colorGrey" href="/terms.html">Terms of Use</a>
								</div>
							</div>
						</div>
					</div>

					<div v-else :key="0" class="signInPage u-full-width">
						<div class="minorTitle">
							Please enter your email address and we will send you a link to
							sign in to your account.
						</div>
						<div class="paddingNormalBottom">
							<ApiResponse :context="'log_in_with_email'" />
						</div>
						<div class="container">
							<div class="row textLeft paddingNormalBottom">
								<div class="twelve columns">
									<div>Email Address</div>
									<input v-model="credentials.verify_email" type="text" name="verify_email" class="hidden" />
									<div class="hidden">Verify Email Address</div>
									<input v-model="credentials.username" type="text" name="email_address" />
								</div>
							</div>
							<div class="row">
								<div class="twelve columns">
									<button v-if="typeof apiPending.log_in_with_email !== 'undefined'"
										class="disabledButton bidButton u-full-width" disabled="disabled">
										<span class="loading" />
									</button>
									<button v-else type="submit" class="bidButton backgroundColorDefault u-full-width"
										@click="passwordlessLogin('log_in_with_email')">
										Continue
									</button>
								</div>
							</div>
							<div class="row marginLargeBottom">
								<div class="paddingLargeTop">
									<a class="navLink" @click="toggleCreateAccount">Create an account</a>
									&nbsp;&nbsp;or&nbsp;&nbsp;
									<a class="navLink" @click="toggleAuthenticateWithPassword">Sign in with a password</a>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="twelve columns textCenter">
								<div class="paddingLargeTop">
									<a class="colorGrey" href="/terms.html">Terms of Use</a>
								</div>
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>
	</div>
</template>

<script>
import ApiResponse from "@/components/ApiResponse.vue";
import CreateAccount from "@/components/Forms/CreateAccount";
import UpdateAccount from "@/components/Forms/UpdateAccount";

export default {
	name: "SignInCard",
	components: {
		ApiResponse,
		CreateAccount,
		UpdateAccount,
	},
	props: ["showUpdateAccount"],
	data() {
		return {
			authenticateWithPassword: true,
			showPasswordlessMessage: false,
			showCreateAccount: false,
			credentials: {
				username: null,
				password: null,
			},
			pin: "",
			verify_email: "",
		};
	},
	computed: {
		authenticated: function () {
			return this.$store.getters.isAuthenticated;
		},
		apiPending: function () {
			return this.$store.getters.apiPending;
		},
		twoFactorRequest: function () {
			return this.$store.getters.twoFactorRequest;
		},
	},
	methods: {
		toggleCreateAccount() {
			this.showCreateAccount = !this.showCreateAccount;
		},
		hideModal() {
			this.$store.dispatch("hideModal");
		},
		toggleAuthenticateWithPassword() {
			this.credentials.username = "";
			this.credentials.password = "";
			this.authenticateWithPassword = !this.authenticateWithPassword;
			this.$store.dispatch("clearApiResponse");
		},
		authenticate(context) {
			let payload = {
				username: this.credentials.username,
				password: this.credentials.password,
				context: context,
			};
			this.$store.dispatch("authenticate", payload).then((value) => {
				// If we receive a true value, we are authenticated but need to verify 2FA pin
				if (value === true) {
					// base 64 encode the username
					window.location.href =
						"/two-factor-verification?account=" +
						btoa(this.credentials.username);
				}
			});
		},
		sendTwoFactorPin(context) {
			let payload = {
				pin: this.pin,
				two_factor_token: this.twoFactorRequest,
				context: context,
			};
			this.$store.dispatch("authenticate", payload);
		},
		passwordlessLogin(context) {
			let payload = {
				username: this.credentials.username,
				password: this.credentials.password,
				verify_email: this.verify_email,
				context: context,
			};
			this.$store.dispatch("passwordlessLogin", payload);
		},
	},
};
</script>

<style scoped></style>

<style scoped></style>
